import React, { useState } from "react";
import dayjs from "dayjs";
import {
  Input,
  Button,
  Space,
  Popconfirm,
  message,
  DatePicker,
  Tag,
  Select, // Added Select import
} from "antd";
import {
  SearchOutlined,
  FilePdfOutlined,
  FileExcelOutlined,
  FileTextOutlined,
  DeleteFilled,
  EditFilled,
} from "@ant-design/icons";
import { ProColumns, ProTable } from "@ant-design/pro-components";
import lang from "src/lang";
import Export from "src/common/components/Export";
import { Link, useParams } from "react-router-dom";
import apiRequest from "src/common/utils/apiRequest";
import coreFunctions from "src/common/utils/coreFunctions";

// Define the TreatmentPlanTableRow interface
interface TreatmentPlanTableRow {
  id: string;
  customer_name: string;
  physiotherapist: string;
  diagnosis: string;
  start_date: string;
  end_date: string;
  total_sessions: number;
  treatment_goals: string;
  progress_notes: string;
  status: "Ongoing" | "Completed" | "Dropped" | "Follow-up";
  added_date: string;
}

const { RangePicker } = DatePicker;
const { Option } = Select; // Added for Select options

const TreatmentPlansTable = ({
  actionRef,
  setTreatmentPlanId,
  form,
  setIsModalVisible,
}: {
  actionRef: any;
  setTreatmentPlanId: (id: string | null) => void;
  form: any;
  setIsModalVisible: (visible: boolean) => void;
}) => {
  const [pageSize, setPageSize] = useState(15);
  const expobj = new Export("Treatment Plans - " + new Date().toDateString());
  const [searchAttributes, setSearchAttributes] = useState<
    { key: string; value: string }[]
  >([]);
  const [allSearch, setAllSearch] = useState<string>("");
  const [filters, setFilters] = useState<
    { key: string; value: any; type: string }[]
  >([]);

  const { id } = useParams();

  // Dummy data for the table
  const dummyData: TreatmentPlanTableRow[] = [
    {
      id: "1",
      customer_name: "John Doe",
      physiotherapist: "Dr. Smith",
      diagnosis: "Lower back pain",
      start_date: "2025-07-01",
      end_date: "2025-08-15",
      total_sessions: 10,
      treatment_goals: "Reduce pain, improve mobility",
      progress_notes: "Patient showing improvement in week 2",
      status: "Ongoing",
      added_date: "2025-06-28",
    },
    {
      id: "2",
      customer_name: "Jane Smith",
      physiotherapist: "Dr. Johnson",
      diagnosis: "Post-surgery rehabilitation",
      start_date: "2025-06-15",
      end_date: "2025-07-30",
      total_sessions: 8,
      treatment_goals: "Regain full range of motion",
      progress_notes: "Completed 5/8 sessions",
      status: "Completed",
      added_date: "2025-06-10",
    },
    {
      id: "3",
      customer_name: "Alice Brown",
      physiotherapist: "Dr. Lee",
      diagnosis: "Sports injury - ACL tear",
      start_date: "2025-07-10",
      end_date: "2025-09-10",
      total_sessions: 12,
      treatment_goals: "Strengthen knee, prevent re-injury",
      progress_notes: "Patient needs to work on flexibility",
      status: "Ongoing",
      added_date: "2025-07-05",
    },
    {
      id: "4",
      customer_name: "Bob Wilson",
      physiotherapist: "Dr. Davis",
      diagnosis: "Chronic neck pain",
      start_date: "2025-05-01",
      end_date: "2025-06-15",
      total_sessions: 6,
      treatment_goals: "Pain management, posture correction",
      progress_notes: "Patient discontinued after 3 sessions",
      status: "Dropped",
      added_date: "2025-04-28",
    },
    {
      id: "5",
      customer_name: "Emma Davis",
      physiotherapist: "Dr. Taylor",
      diagnosis: "Post-stroke recovery",
      start_date: "2025-08-01",
      end_date: "2025-10-01",
      total_sessions: 15,
      treatment_goals: "Improve motor function, balance",
      progress_notes: "Initial assessment completed",
      status: "Follow-up",
      added_date: "2025-07-25",
    },
  ];

  const [tableData, setTableData] =
    useState<TreatmentPlanTableRow[]>(dummyData);

  const handleSearch = (field: string, value: string) => {
    setAllSearch("");
    const existingAttribute = searchAttributes.find(
      (attr) => attr.key === field
    );

    if (existingAttribute) {
      setSearchAttributes(
        searchAttributes.map((attr) =>
          attr.key === field ? { ...attr, value } : attr
        )
      );
    } else {
      setSearchAttributes([...searchAttributes, { key: field, value }]);
    }
  };

  const searchCols = (field: string, label: string) => ({
    filterDropdown: (props: any) => {
      const { close } = props;
      return (
        <div onKeyDown={(e) => e.stopPropagation()}>
          <Input
            placeholder={lang.get("hr", [`Search ${label}`])}
            onChange={(e) => handleSearch(field, e.target.value)}
            value={searchAttributes.find((item) => item.key === field)?.value}
            style={{ marginBottom: 8, display: "block" }}
            onKeyUp={(e) => {
              if (e.key === "Enter") {
                close();
                actionRef.current?.reload();
              }
            }}
          />
          <Space>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              size="small"
              onClick={() => actionRef.current?.reload()}
              style={{ width: 90 }}
            >
              {lang.get("hr", ["Search"])}
            </Button>
            <Button onClick={() => close()} size="small" style={{ width: 90 }}>
              {lang.get("hr", ["Close"])}
            </Button>
          </Space>
        </div>
      );
    },
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
  });

  const handleEdit = (record: TreatmentPlanTableRow) => {
    setIsModalVisible(true);
    setTreatmentPlanId(record.id);
    form.setFieldsValue({
      ...record,
      start_date: dayjs(record.start_date),
      end_date: dayjs(record.end_date),
      added_date: dayjs(record.added_date),
    });
  };

  const handleDelete = (id: string) => {
    const API_REQUEST = new apiRequest("hr/treatment-plan/delete", true);
    API_REQUEST.send(
      {
        id,
      },
      (response: { status: string; message: string; hr_record: string }) => {
        if (response && response.status === "success") {
          actionRef.current?.reload();
          message.success(response.message);
        } else {
          message.error(response.message);
        }
      }
    );
  };

  const statusColors = {
    Ongoing: "blue",
    Completed: "green",
    Dropped: "red",
    "Follow-up": "orange",
  };

  const columns: ProColumns<TreatmentPlanTableRow>[] = [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
      sorter: false,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Customer Name",
      dataIndex: "customer_name",
      key: "customer_name",
      sorter: true,
      ...searchCols("customer_name", "Customer Name"),
    },
    {
      title: "Assigned Physiotherapist",
      dataIndex: "physiotherapist",
      key: "physiotherapist",
      sorter: true,
      ...searchCols("physiotherapist", "Physiotherapist"),
    },
    {
      title: "Diagnosis / Condition",
      dataIndex: "diagnosis",
      key: "diagnosis",
      sorter: true,
      ...searchCols("diagnosis", "Diagnosis"),
    },
    {
      title: "Start & End Dates",
      dataIndex: "date_range",
      key: "date_range",
      sorter: true,
      render: (_, record) => (
        <>
          <div>{dayjs(record.start_date).format("MMM D, YYYY")}</div>
          <div>to</div>
          <div>{dayjs(record.end_date).format("MMM D, YYYY")}</div>
        </>
      ),
      filterDropdown: (props: any) => {
        const { close } = props;
        return (
          <div onKeyDown={(e) => e.stopPropagation()}>
            <RangePicker
              onChange={(dates) => {
                if (dates) {
                  setFilters([
                    ...filters.filter((f) => f.key !== "date_range"),
                    {
                      key: "date_range",
                      value: dates.map((date) => date?.toISOString()),
                      type: "range",
                    },
                  ]);
                } else {
                  setFilters(filters.filter((f) => f.key !== "date_range"));
                }
              }}
              style={{ marginBottom: 8, display: "block" }}
            />
            <Space>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                size="small"
                onClick={() => actionRef.current?.reload()}
                style={{ width: 90 }}
              >
                {lang.get("hr", ["Search"])}
              </Button>
              <Button
                onClick={() => close()}
                size="small"
                style={{ width: 90 }}
              >
                {lang.get("hr", ["Close"])}
              </Button>
            </Space>
          </div>
        );
      },
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
      ),
    },
    {
      title: "Total Sessions",
      dataIndex: "total_sessions",
      key: "total_sessions",
      sorter: true,
      ...searchCols("total_sessions", "Total Sessions"),
    },
    {
      title: "Treatment Goals",
      dataIndex: "treatment_goals",
      key: "treatment_goals",
      sorter: true,
      ...searchCols("treatment_goals", "Treatment Goals"),
      ellipsis: true,
    },
    {
      title: "Progress Notes",
      dataIndex: "progress_notes",
      key: "progress_notes",
      sorter: true,
      ...searchCols("progress_notes", "Progress Notes"),
      ellipsis: true,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      sorter: true,
    },
    {
      title: "Added Date",
      dataIndex: "added_date",
      key: "added_date",
      sorter: true,
      ...searchCols("added_date", "Added Date"),
    },
  ];

  return (
    <>
      <ProTable
        columns={columns}
        actionRef={actionRef}
        dataSource={tableData}
        request={async (params, sort, filter) => {
          await new coreFunctions().waitRequest(10);

          const processedFilters = Object.entries(filter || {}).reduce(
            (acc, [key, value]) => {
              if (value && value.length > 0) {
                acc[key] = value;
              }
              return acc;
            },
            {} as Record<string, (string | number)[]>
          );

          const payload = {
            other_filters: filters,
            params: params,
            sorts: sort,
            filters: processedFilters,
            searches: searchAttributes,
            all_search: allSearch,
            hr_record: id,
          };

          // Simulate API response with dummy data
          let filteredData = dummyData;

          // Apply global search
          if (allSearch) {
            filteredData = filteredData.filter((row) =>
              Object.values(row).some((value) =>
                value
                  ?.toString()
                  .toLowerCase()
                  .includes(allSearch.toLowerCase())
              )
            );
          }

          // Apply column-specific searches
          searchAttributes.forEach(({ key, value }) => {
            if (value) {
              filteredData = filteredData.filter((row) =>
                row[key as keyof TreatmentPlanTableRow]
                  ?.toString()
                  .toLowerCase()
                  .includes(value.toLowerCase())
              );
            }
          });

          // Apply filters (including status filter from column)
          Object.entries(processedFilters).forEach(([key, value]) => {
            filteredData = filteredData.filter((row) =>
              (value as string[]).includes(
                row[key as keyof TreatmentPlanTableRow]?.toString()
              )
            );
          });

          // Apply date range filter
          const dateFilter = filters.find((f) => f.key === "date_range");
          if (dateFilter && dateFilter.value) {
            const [start, end] = dateFilter.value;
            filteredData = filteredData.filter((row) => {
              const startDate = dayjs(row.start_date);
              const endDate = dayjs(row.end_date);
              return (
                (startDate.isAfter(dayjs(start)) ||
                  startDate.isSame(dayjs(start))) &&
                (endDate.isBefore(dayjs(end)) || endDate.isSame(dayjs(end)))
              );
            });
          }

          // Apply status filter from toolbar
          const statusFilter = filters.find((f) => f.key === "status");
          if (statusFilter && statusFilter.value) {
            filteredData = filteredData.filter(
              (row) => row.status === statusFilter.value
            );
          }

          // Apply sorting
          if (Object.keys(sort).length > 0) {
            filteredData = [...filteredData].sort((a, b) => {
              const key = Object.keys(sort)[0] as keyof TreatmentPlanTableRow;
              const order = sort[key] === "ascend" ? 1 : -1;
              return a[key] > b[key] ? order : -order;
            });
          }

          // Apply pagination
          const page = params.current || 1;
          const size = params.pageSize || pageSize;
          const startIndex = (page - 1) * size;
          const paginatedData = filteredData.slice(
            startIndex,
            startIndex + size
          );

          setTableData(paginatedData);

          return {
            data: paginatedData,
            total: filteredData.length,
            success: true,
          };
        }}
        rowKey="id"
        pagination={{
          defaultCurrent: 1,
          pageSize: pageSize,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50],
          onShowSizeChange: (_, size) => setPageSize(size),
        }}
        search={false}
        scroll={{ x: "max-content" }}
        toolBarRender={() => [
          <Space size="middle" wrap>
            <Input
              placeholder="Global Search"
              onChange={(e) => {
                setAllSearch(e.target.value);
                actionRef.current?.reload();
              }}
              style={{ width: 150 }}
              prefix={<SearchOutlined />}
            />
            <RangePicker
              onChange={(dates) => {
                if (dates) {
                  setFilters([
                    ...filters.filter((f) => f.key !== "date_range"),
                    {
                      key: "date_range",
                      value: dates.map((date) => date?.toISOString()),
                      type: "range",
                    },
                  ]);
                } else {
                  setFilters(filters.filter((f) => f.key !== "date_range"));
                }
                actionRef.current?.reload();
              }}
            />
            <Select
              placeholder="Filter by Status"
              style={{ width: 150 }}
              allowClear
              onChange={(value) => {
                if (value) {
                  setFilters([
                    ...filters.filter((f) => f.key !== "status"),
                    {
                      key: "status",
                      value: value,
                      type: "select",
                    },
                  ]);
                } else {
                  setFilters(filters.filter((f) => f.key !== "status"));
                }
                actionRef.current?.reload();
              }}
            >
              <Option value="Ongoing">Ongoing</Option>
              <Option value="Completed">Completed</Option>
              <Option value="Dropped">Dropped</Option>
              <Option value="Follow-up">Follow-up</Option>
            </Select>
            <Button
              key="csv"
              icon={<FileTextOutlined />}
              onClick={() => {
                const csvData = tableData.map((row) => ({
                  id: row.id,
                  customer_name: row.customer_name,
                  physiotherapist: row.physiotherapist,
                  diagnosis: row.diagnosis,
                  start_date: row.start_date,
                  end_date: row.end_date,
                  total_sessions: row.total_sessions,
                  treatment_goals: row.treatment_goals,
                  progress_notes: row.progress_notes,
                  status: row.status,
                  added_date: row.added_date,
                }));
                expobj.exportToCSV(csvData);
              }}
            >
              Export CSV
            </Button>
            <Button
              key="excel"
              icon={<FileExcelOutlined />}
              onClick={() => {
                const excelData = tableData.map((row) => ({
                  id: row.id,
                  customer_name: row.customer_name,
                  physiotherapist: row.physiotherapist,
                  diagnosis: row.diagnosis,
                  start_date: row.start_date,
                  end_date: row.end_date,
                  total_sessions: row.total_sessions,
                  treatment_goals: row.treatment_goals,
                  progress_notes: row.progress_notes,
                  status: row.status,
                  added_date: row.added_date,
                }));
                expobj.exportToExcel(excelData);
              }}
            >
              Export Excel
            </Button>
            <Button
              key="pdf"
              icon={<FilePdfOutlined />}
              onClick={() => {
                expobj.exportToPDF({
                  head: [
                    "ID",
                    "Customer Name",
                    "Physiotherapist",
                    "Diagnosis",
                    "Start Date",
                    "End Date",
                    "Total Sessions",
                    "Treatment Goals",
                    "Progress Notes",
                    "Status",
                    "Added Date",
                  ],
                  body: tableData.map((row) => [
                    row.id,
                    row.customer_name,
                    row.physiotherapist,
                    row.diagnosis,
                    dayjs(row.start_date).format("MMM D, YYYY"),
                    dayjs(row.end_date).format("MMM D, YYYY"),
                    row.total_sessions,
                    row.treatment_goals,
                    row.progress_notes,
                    row.status,
                    dayjs(row.added_date).format("MMM D, YYYY"),
                  ]),
                });
              }}
            >
              Export PDF
            </Button>
          </Space>,
        ]}
      />
    </>
  );
};

export default TreatmentPlansTable;
